import { useQuery, type WatchQueryFetchPolicy } from '@provision/graphql-client';
import { useMemo } from 'react';
import { isNullish } from 'remeda';
import { GET_WORKFLOWS_QUERY } from 'src/graphql/entities/workflow/workflow.operations';
import { GetWorkflowsQuery } from 'src/graphql/entities/workflow/workflow.types';
import { QueryVariables } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';

interface UseWorkflowsOptions {
  /**
   * Include the platform partner in the request's isolation scope.
   *
   * Required to read partner 0's workflows from any other partner: the `ov-partner-ids`
   * header is built from the selected partner alone unless this is set, so a
   * `partnerId = 0` filter silently matches nothing. Off by default — the workflows page
   * lists what the selected partner owns, and platform rows appearing there would be a
   * change nobody asked for.
   */
  includePlatformLevelData?: boolean;
  /**
   * Apollo fetch policy. Callers that must not serve a stale list — anything opened after a
   * version was activated or deleted elsewhere on the page — pass `cache-and-network`.
   */
  fetchPolicy?: WatchQueryFetchPolicy;
}

export const useWorkflows = (
  partnerId?: number | null,
  { includePlatformLevelData = false, fetchPolicy }: UseWorkflowsOptions = {},
) => {
  const { showErrorAlert } = useNotifications();

  const {
    data,
    loading,
    error,
    refetch: refetchWorkflows,
  } = useQuery<GetWorkflowsQuery, QueryVariables>(GET_WORKFLOWS_QUERY, {
    variables: isNullish(partnerId)
      ? undefined
      : {
          params: {
            filters: [
              {
                attribute: 'partnerId',
                operator: '=',
                value: partnerId.toString(),
              },
            ],
            sorting: [{ attribute: 'name' }],
          },
        },
    skip: isNullish(partnerId),
    fetchPolicy,
    // `ttl: 0`: as for the versions, a refetch after a create must reach the server rather than
    // the 12h TTL link cache, or a just-created workflow never shows up in the list.
    context: { includePlatformLevelData, ttl: 0 },
    onError: (fetchError) => {
      console.error(fetchError);
      showErrorAlert('Failed to load workflows. Please try again.');
    },
  });

  const workflows = useMemo(
    () => data?.getWorkflow?.records ?? [],
    [data?.getWorkflow?.records],
  );

  return {
    workflows,
    loading,
    error,
    refetchWorkflows,
  };
};
